import React from "react";
import Layout from "../components/Layout";
import { useState, useEffect } from "react";
import Papa from "papaparse";
import { Helmet } from "react-helmet-async";


/**
 * Fetches a CSV file, extracts the last tab count, and updates state.
 * @param {string} url - The path to the CSV file.
 * @param {Function} setTabs - State setter for the tab count.
 */
const fetchLastTabCount = (setTabs) => {
  fetch("/uploads/firefox_tab_log.csv")
    .then(response => response.text())
    .then(csvText => {
      Papa.parse(csvText, {
        complete: (result) => {
          if (result.data.length > 0) {
            const lastRow = result.data.filter(row => row.length > 0).at(-1);
            if (lastRow && lastRow.length > 1) {
              const tabCount = parseInt(lastRow[1].trim(), 10);
              if (!isNaN(tabCount)) {
                setTabs(tabCount);
              }
            }
          }
        },
        header: false,
        skipEmptyLines: true,
      });
    })
    .catch(error => console.error("Error fetching CSV:", error));
};


/**
 * Fetches my_stats.json and updates state variables.
 * @returns {Promise<{download_folder_size: number, last_report: string}>}
 */
const fetchMyStats = async (signal) => {
  try {
    const response = await fetch("/uploads/my_stats.json", { signal });
    if (!response.ok) throw new Error("Failed to fetch stats");

    return response.json();
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Fetch aborted");
    } else {
      console.error("Error fetching my_stats.json:", error);
    }
    return { download_folder_size: 0, last_report: "" }; // Default values on error
  }
};

/**
 * Returns the absolute number of days between today and a given date.
 * @param {string} date - Date in "DD/MM/YYYY" format.
 * @returns {number} - Always a positive number (days between today and the given date).
 */
const getNbDaysBetweenTodayAndDate = (date) => {
  if (!date) return 0;

  const [day, month, year] = date.split("/").map(Number);
  const givenDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInMs = Math.abs(today - givenDate); // Absolute difference
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24)); // Convert ms to days
};


function LifeMonitoring() {
  const [nbTabs, setNbTabs] = useState("Calculating...");

  useEffect(() => {
    fetchLastTabCount(setNbTabs);
  }, []);

  const [downloadFolderSize, setDownloadFolderSize] = useState("Calculating...");
  const [daysSinceReport, setDaysSinceReport] = useState("Calculating...");
  const [lastReportEmoji, setLastReportEmoji] = useState("🤔");

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
      const data = await fetchMyStats(signal);
      setDownloadFolderSize(data.download_folder_size);
      const days = getNbDaysBetweenTodayAndDate(data.last_report);
      setDaysSinceReport(days);

      if (days === 0) {
        setLastReportEmoji("✅")
      }
      else if (days >= 1 && days < 3) {
        setLastReportEmoji("🙂")
      }
      else if (days >= 3 && days < 7) {
        setLastReportEmoji("😯")
      }
      else if (days >= 7) {
        setLastReportEmoji("😬")
      }
    };

    fetchData();

    // Cleanup function to abort fetch on unmount
    return () => controller.abort();
  }, []);

  // Here: the age thing
  const [systemClock, setSystemClock] = useState("Calculating...");
  const [distanceTravelled, setDistanceTravelled] = useState("Calculating...");
  const [gigameterBorn, setGigameterBorn] = useState("Calculating...");

  useEffect(() => {
    const nbDaysAlive = getNbDaysBetweenTodayAndDate("21/02/1996")
    setSystemClock(nbDaysAlive)
    const degreeTravelled = 360 * nbDaysAlive / 365.25
    setDistanceTravelled(Math.floor(degreeTravelled).toLocaleString('fr-FR'))

    const currentYear = new Date().getFullYear()
    const nbDaysBetweenBirthdayThisYear = getNbDaysBetweenTodayAndDate("21/02/" + currentYear)
    const nbDaysBetweenBirthdayNextYear = getNbDaysBetweenTodayAndDate("21/02/" + (currentYear+1))
    const closestBirthday = Math.min(nbDaysBetweenBirthdayThisYear, nbDaysBetweenBirthdayNextYear)
    const earthDistanceTravelledInAYear = 939.886
    const howFarIAm = earthDistanceTravelledInAYear * closestBirthday / 365.25
    setGigameterBorn(Number(howFarIAm.toFixed(1)))

  }, []);

  const [nbDaysBeforeNextKagerou, setNbDaysBeforeNextKagerou] = useState("Calculating...");

  useEffect(() => {
    const currentYear = new Date().getFullYear()
    const today = new Date();
    let nextKagerouDazeDate = "14/08/" + currentYear
    if (today === nextKagerouDazeDate) {
      setNbDaysBeforeNextKagerou("NOW!");
      return;
    }
    else if (today > nextKagerouDazeDate) {
      nextKagerouDazeDate = "14/08/" + (currentYear+1)
    }
    setNbDaysBeforeNextKagerou(getNbDaysBetweenTodayAndDate(nextKagerouDazeDate))

  }, []);


  return (
    <Layout>
      <Helmet>
        <title>Life Monitoring | Croquette</title>
      </Helmet>
      <header className="text-3xl font-bold text-blue-600 mb-8">
        Soul Sample
      </header>

      <p>Internal weather: Sunny</p>
      <p>System clock: {systemClock}</p>
      <p>Life color: Yellow</p>
      <p>Current arc: ICE CREAM SCOOPER in an endless summer!!!</p>
      <p>Mental state: Alchemist</p>
      <p>Insert Overwhelming Hype Here</p>
      <p>Theme song: <a
          className="text-blue-500 underline hover:text-blue-700"
          href="https://youtu.be/NF6KQiTfrFk"
          target="_blank"
          rel="noopener noreferrer"
        >
          Medieval Wave - ALOUTOULOU
        </a>
      </p>
      <p>Last report: {daysSinceReport} days ago {lastReportEmoji}</p>
      <p>Number of tabs on browser: {nbTabs}</p>
      <p>Download folder: {downloadFolderSize} files</p>
      <p>{nbDaysBeforeNextKagerou} days before Kagerou Days</p>
      <p>Distance travelled: {distanceTravelled}°</p>
      <p>Born {gigameterBorn} Gigameters away on Sol III</p>
    </Layout>
  );
}

export default LifeMonitoring;
